/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbBooleanLogicGroupingContent } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicGroupingContent";
import { AbBooleanLogicSingleConditionContent } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicSingleConditionContent";
import { AbBooleanLogicTreeContent } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicTreeContent";
import { SelectionMachineAction } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicTreeSelectionMachine";
import { Assert } from "abweb/utils/Assert";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { NullUtils } from "abweb/utils/NullUtils";
import * as Immutable from "immutable";
var ReconciledBooleanTreeContent = SelectionMachineAction.ReconciledBooleanTreeContent;
// Typescript doesn't allow a defaults object to be templatized
const BooleanLogicTreeContentDefaults = {
  treeContent: new AbBooleanLogicTreeContent(),
  itemContentTable: Immutable.Map([])
};
export class BooleanLogicTreeContent extends Immutable.Record(BooleanLogicTreeContentDefaults) {
  constructor() {
    const fields = {};
    super(fields);
  }
  // helper method for strong typing
  with(fields) {
    return this.merge(fields);
  }
  withTreeContent(value) {
    return this.with({
      treeContent: value
    });
  }
  withItemTable(value) {
    return this.with({
      itemContentTable: value
    });
  }
  getItemContent(conditionKey) {
    // workaround to wrong return type for Immutable.Map.get, which should allow undefined to be returned
    return this.itemContentTable.get(conditionKey, undefined);
  }
  withItemContent(conditionKey, conditionContent) {
    return this.with({
      itemContentTable: this.itemContentTable.set(conditionKey, conditionContent)
    });
  }
  withDuplicatedItem(conditionKey, newConditionKey) {
    const itemToDuplicate = this.getItemContent(conditionKey);
    return itemToDuplicate ? this.withItemContent(newConditionKey, itemToDuplicate) : this;
  }
  withItemContentRemoved(conditionKey) {
    return this.with({
      itemContentTable: this.itemContentTable.delete(conditionKey)
    });
  }
  keysInTreeButNotTable() {
    let keysFound = Immutable.Set();
    this.treeContent.visitAllConditions(conditionKey => {
      keysFound = keysFound.add(conditionKey);
    });
    let keysInTable = Immutable.Set(this.itemContentTable.keys());
    return keysFound.subtract(keysInTable);
  }
  keysInTableButNotTree() {
    let keysFound = Immutable.Set();
    this.treeContent.visitAllConditions(conditionKey => {
      keysFound = keysFound.add(conditionKey);
    });
    let keysInTable = Immutable.Set(this.itemContentTable.keys());
    return keysInTable.subtract(keysFound);
  }
  assertKeysConsistent() {
    let keysFound = Immutable.Set();
    this.treeContent.visitAllConditions(conditionKey => {
      keysFound = keysFound.add(conditionKey);
    });
    let keysInTable = Immutable.Set(this.itemContentTable.keys());
    const a = keysFound.subtract(keysInTable);
    const b = keysInTable.subtract(keysFound);
    Assert.assert(a.count() === 0);
    Assert.assert(b.count() === 0);
  }
  indentSelectedGuidedExpressionContent(selectionMachine) {
    // Creates to-return TreeLogic data structure, Item Content is unchanged
    let treeLogicRet;
    treeLogicRet = this.recursiveIndentTreeContent(this.treeContent.nestingState, selectionMachine);
    let retBinaryLogicContent = this;
    retBinaryLogicContent = retBinaryLogicContent.withTreeContent(new AbBooleanLogicTreeContent(treeLogicRet));
    return retBinaryLogicContent;
  }
  recursiveIndentTreeContent(treeLogic, selectionMachine) {
    let groupingContentContainsSelection = false;
    for (let index = 0; index < treeLogic.sequence.size; index++) {
      let item = treeLogic.sequence.get(index);
      if (ClassUtils.isInstanceOf(item, AbBooleanLogicSingleConditionContent) && selectionMachine.isSelected(item.key)) groupingContentContainsSelection = true;else if (ClassUtils.isInstanceOf(item, AbBooleanLogicGroupingContent)) {
        const ret = AbBooleanLogicGroupingContent.analyzeSelection(item, selectionMachine);
        if (ret.allSelected) groupingContentContainsSelection = true;
      }
    }
    if (groupingContentContainsSelection) return this.indentSelection(treeLogic, selectionMachine);

    // Go Further into the nested structure
    for (let index = 0; index < treeLogic.sequence.size; index++) {
      let item = treeLogic.sequence.get(index);
      if (ClassUtils.isInstanceOf(item, AbBooleanLogicGroupingContent)) {
        if (this.getSelectedContent(selectionMachine, item).treeContent.nestingState.sequence.size > 0) {
          return treeLogic.withGroupItemSetAt(index, this.recursiveIndentTreeContent(item, selectionMachine));
        }
      }
    }
    Assert.unexpected("Indent was invoked on expression with no active selection");
  }
  indentSelection(treeLogic, selectionMachine) {
    const connective = treeLogic.connective === "AND" ? "OR" : "AND";
    let treeLogicRet = new AbBooleanLogicGroupingContent(selectionMachine.generateSaveAndReturnID("GROUP"), connective);
    let target = undefined;
    for (let index = 0; index < treeLogic.sequence.size; index++) {
      let item = treeLogic.sequence.get(index);
      if (ClassUtils.isInstanceOf(item, AbBooleanLogicSingleConditionContent) && selectionMachine.isSelected(item.key)) {
        if (target === undefined) target = index;
        treeLogicRet = treeLogicRet.withAppendSingleRow(item);
        treeLogic = treeLogic.withItemRemovedAt(index);
        index--;
      } else if (ClassUtils.isInstanceOf(item, AbBooleanLogicGroupingContent)) {
        if (this.getSelectedContent(selectionMachine, item).treeContent.nestingState.sequence.size > 0) {
          if (target === undefined) target = index;
          treeLogicRet = treeLogicRet.withAppendSubgroup(item);
          treeLogic = treeLogic.withItemRemovedAt(index);
          index--;
        }
      }
    }
    return treeLogic.withSubgroupInsertedAt(NullUtils.assertDefined(target), treeLogicRet);
  }
  disableOrEnableTreeContent(selectionMachine, action) {
    const conditionAction = condition => {
      return condition.withDisabled(action === "disable");
    };
    const conditionalCheck = condition => {
      return selectionMachine.currentSelection.includes(condition.key);
    };
    let treeLogicRet;
    treeLogicRet = this.treeContent.nestingState.performConditionalActionOnRows(conditionAction, conditionalCheck);
    let retBinaryLogicContent = this;
    retBinaryLogicContent = retBinaryLogicContent.withTreeContent(new AbBooleanLogicTreeContent(treeLogicRet));
    return retBinaryLogicContent;
  }
  removeSelectedGuidedExpressionContent(selectionMachine) {
    let groupingContentRet = this.treeContent.nestingState.removeRowsByKey(selectionMachine.currentSelection);
    let itemTableRet = this.itemContentTable;
    selectionMachine.currentSelection.forEach(key => {
      itemTableRet = itemTableRet.remove(key);
    });
    let retBinaryLogicContent = this;
    retBinaryLogicContent = retBinaryLogicContent.withTreeContent(new AbBooleanLogicTreeContent(groupingContentRet));
    retBinaryLogicContent = retBinaryLogicContent.withItemTable(itemTableRet);
    return retBinaryLogicContent;
  }
  reconcileIncomingBooleanLogicTreeContent(selectionMachine, booleanLogicTreeContentToIngest) {
    const newConditionTreeNodes = [];
    const newConditionDataItems = [];
    const newConditionKeys = [];
    /** Extract list of to-be-inserted condition keys from nested tree structure,
     * Generate key if a match exists to avoid duplicates **/
    let newConditionKeyDisabledMap = Immutable.Map();
    const extractInfoFromNestedStructure = node => {
      if (ClassUtils.isInstanceOf(node, AbBooleanLogicSingleConditionContent)) {
        let newKey = selectionMachine.generateNewID();
        newConditionKeys.push(newKey);
        newConditionKeyDisabledMap = newConditionKeyDisabledMap.set(newKey, node.disabled);
      }
    };
    /** generate set of to-insert condition keys */
    booleanLogicTreeContentToIngest.treeContent.visitAllNodes(extractInfoFromNestedStructure);
    /** generate set of to-insert condition items */
    const ItemsToAdd = BooleanLogicTreeContent.flattenFullLogicTreeContent(booleanLogicTreeContentToIngest);
    /** generate set of to-insert condition tree nodes */
    newConditionKeys.forEach((key, index) => {
      selectionMachine.addConditionKeyToAdditionalInfo(key);
      newConditionDataItems.push([key, ItemsToAdd[index]]);
      newConditionTreeNodes.push(new AbBooleanLogicSingleConditionContent(key).withDisabled(newConditionKeyDisabledMap.get(key)));
    });
    return {
      newConditionDataItems,
      newConditionTreeNodes
    };
  }
  insertSelectedGuidedExpressionContent(selectionMachine, targetLocation, reconciledBooleanTreeContent) {
    let groupingContentRet;
    let itemTableRet;

    /** Set return values **/
    groupingContentRet = this.treeContent.nestingState.insertRowsAtTreeLocation(targetLocation, reconciledBooleanTreeContent.newConditionTreeNodes);
    itemTableRet = this.itemContentTable;
    reconciledBooleanTreeContent.newConditionDataItems.forEach(conditionData => {
      itemTableRet = itemTableRet.set(conditionData[0], conditionData[1]);
    });
    let retBinaryLogicContent = this;
    retBinaryLogicContent = retBinaryLogicContent.withTreeContent(new AbBooleanLogicTreeContent(groupingContentRet));
    retBinaryLogicContent = retBinaryLogicContent.withItemTable(itemTableRet);
    return retBinaryLogicContent;
  }
  moveSelectedGuidedExpressionContent(selectionMachine, dropTargetLocation, reconciledBooleanTreeContent) {
    return this.insertSelectedGuidedExpressionContent(selectionMachine, dropTargetLocation, reconciledBooleanTreeContent).removeSelectedGuidedExpressionContent(selectionMachine);
  }
  getSelectedContent(selectionMachine, nestingState) {
    let retBinaryLogicContent = new BooleanLogicTreeContent();
    retBinaryLogicContent = retBinaryLogicContent.withTreeContent(new AbBooleanLogicTreeContent(nestingState.getRowsByKey(selectionMachine.currentSelection)));
    selectionMachine.currentSelection.forEach(key => {
      retBinaryLogicContent = retBinaryLogicContent.withItemTable(retBinaryLogicContent.itemContentTable.set(key, this.itemContentTable.get(key)));
    });
    return retBinaryLogicContent;
  }
  static flattenFullLogicTreeContent(clipboardData) {
    let conditionData = [];
    clipboardData.treeContent.visitAllConditions(key => {
      conditionData.push(clipboardData.itemContentTable.get(key));
    });
    return conditionData;
  }
}
//# sourceMappingURL=BooleanLogicTreeContent.js.map
